import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useOnceMutation, useRunOnce } from '@vnetwork/margarita-shell/once-mutation';
import { Button, Input } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import { AuthShell } from './components/AuthShell';
import { forgotPassword } from '@/api/auth-flow';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{ email: string }>({ defaultValues: { email: '' } });

  const { mutateAsync, isPending } = useOnceMutation({ mutationFn: forgotPassword });
  const once = useRunOnce();

  const onSubmit = (values: { email: string }) =>
    once(async () => {
      setError(null);
      try {
        await mutateAsync(values);
        // Always show success to avoid leaking which emails exist.
        setSent(true);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Something went wrong.');
      }
    });

  return (
    <AuthShell
      title="Forgot Password"
      subtitle="Enter your email address and we'll send you a link to reset your password."
      footer={
        <>
          Remember your password?{' '}
          <Link
            to="/auth/login"
            style={{ color: 'var(--vn-primary-text)', textDecoration: 'none' }}
          >
            Back to Login
          </Link>
        </>
      }
    >
      {sent ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <Alert tone="success" title="Check your inbox">
            If an account exists for that email, a reset link is on its way.
          </Alert>
          <Button asChild variant="secondary" fullWidth>
            <Link to="/auth/login">Back to sign in</Link>
          </Button>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{ display: 'grid', gap: 'var(--vn-space-4)' }}
        >
          {error ? <Alert tone="danger" title={error} /> : null}
          <Field label="Email" required error={errors.email?.message}>
            {(p) => (
              <Input
                {...p}
                type="email"
                placeholder="you@company.com"
                autoComplete="email"
                invalid={!!errors.email}
                {...register('email', {
                  required: "Email can't be empty.",
                  pattern: { value: EMAIL_RE, message: 'Email invalid.' },
                })}
              />
            )}
          </Field>
          <Button type="submit" variant="primary" fullWidth loading={isPending}>
            Send Reset Link
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
